import axios from 'axios';
import {getApiBaseUrl} from '../utils/getApiBaseUrl';
import secureStorage from "../utils/secureStorage";

// URL base de la API
// const API_URL = getApiBaseUrl();
const API_URL = getApiBaseUrl();


// Crear una instancia de Axios
const api = axios.create({
  baseURL: API_URL,
});

// Configurar un interceptor para agregar el token a cada solicitud
api.interceptors.request.use(
  (config) => {
    // Obtener el token del localStorage
    const token = secureStorage.getItem('authToken');
    
    if (token) {
      // Agregar el token al encabezado de autorización
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const getErrorMessage = (error, fallbackMessage) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  return `${fallbackMessage}${error?.message ? `: ${error.message}` : ''}`;
};


const getDatosUser = async () => {
  try {
    const response = await api.get(`${API_URL}user`);
    return response.data;
  } catch (error) {
    throw new Error('Error al obtener datos del backend:', error);
  }
};


const getUserView = async () => {
  try {
    const response = await api.get('viewUser');
    return response.data;
  } catch (error) {
    throw new Error('Error al obtener datos del backend: ' + error.message);
  }
};

const getOneDatos = async (id) => {
  try {
    const response = await api.get(`${API_URL}garden/${id}`);
    return response.data;
  } catch (error) {
    throw new Error('Error al obtener datos del backend:', error);
  }
};

const PostDatosStockLaboratorio = async (datos) => {
  try {
    const response = await api.post('stock/Laboratorio', datos);

    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al enviar datos al backend'));
  }
};

const getPlantillaPruebas = async () => {
  try {
    const response = await api.get('laboratorio/plantillaPruebas');
    return response.data;
  } catch (error) {
    return [
      { codigo: 'espesor_total', etiqueta: 'Espesor total (mm)', tipo: 'number', requerido: true },
      { codigo: 'adhesion_pvb', etiqueta: 'Adhesión PVB', tipo: 'select', opciones: ['APROBADO', 'REPROBADO'], requerido: true },
      { codigo: 'transmitancia_luz', etiqueta: 'Transmitancia de luz (%)', tipo: 'number', requerido: false },
      { codigo: 'observaciones', etiqueta: 'Observaciones', tipo: 'text', requerido: false },
    ];
  }
};

const getCatalogoPruebasLaboratorio = async (incluirInactivas = false) => {
  try {
    const response = await api.get('laboratorio/pruebas', {
      params: { incluirInactivas },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al obtener el catalogo de pruebas'));
  }
};

const createPruebaLaboratorio = async (payload) => {
  try {
    const response = await api.post('laboratorio/pruebas', payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al crear la prueba'));
  }
};

const updatePruebaLaboratorio = async (id, payload) => {
  try {
    const response = await api.put(`laboratorio/pruebas/${id}`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al actualizar la prueba'));
  }
};

const deletePruebaLaboratorio = async (id) => {
  try {
    const response = await api.delete(`laboratorio/pruebas/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al desactivar la prueba'));
  }
};

const getChapaContextoLaboratorio = async (id_produccion) => {
  try {
    const response = await api.get(`laboratorio/chapas/${encodeURIComponent(id_produccion)}/contexto`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al obtener el contexto de la chapa'));
  }
};

const getDatosSerieLaboratorio = async (id_produccion) => {
  try {
    const response = await api.get(`getSerieEtiqueta/serie/${encodeURIComponent(id_produccion)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const createEnsayoLaboratorio = async (payload) => {
  try {
    const response = await api.post('laboratorio/ensayos', payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al crear el ensayo de laboratorio'));
  }
};

const getEnsayosLaboratorio = async (params = {}) => {
  try {
    const response = await api.get('laboratorio/ensayos', { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al obtener ensayos de laboratorio'));
  }
};

const getEnsayoLaboratorioById = async (id) => {
  try {
    const response = await api.get(`laboratorio/ensayos/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al obtener detalle del ensayo'));
  }
};

const getResultadosVistaEnsayoLaboratorio = async (idEnsayo) => {
  try {
    const response = await api.get(`laboratorio/ensayos/${idEnsayo}/resultados-vista`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al obtener resultados vista del ensayo'));
  }
};

const seleccionarPruebasEnsayoLaboratorio = async (idEnsayo, idsPruebaCatalogo) => {
  try {
    const response = await api.post(`laboratorio/ensayos/${idEnsayo}/pruebas`, {
      ids_prueba_catalogo: idsPruebaCatalogo,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al asignar pruebas al ensayo'));
  }
};

const guardarBloqueEnsayoLaboratorio = async (idEnsayo, payload) => {
  try {
    const response = await api.post(`laboratorio/ensayos/${idEnsayo}/bloques`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al guardar bloque del ensayo'));
  }
};

const cerrarEnsayoLaboratorio = async (idEnsayo, payload) => {
  try {
    const response = await api.post(`laboratorio/ensayos/${idEnsayo}/cerrar`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al cerrar el ensayo'));
  }
};

const getResultadosLaboratorio = async (serie) => {
  try {
    const response = await api.get('laboratorio/resultados', {
      params: { serie },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al obtener los resultados de laboratorio'));
  }
};

const postResultadosLaboratorio = async (payload, options = {}) => {
  try {
    const response = await api.post('laboratorio/resultados', payload, {
      params: {
        retest: options?.retest ? true : undefined,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Error al enviar resultados'));
  }
};

const putDatosUser = async (id, data) => {
  try {
    const response = await api.put(`${API_URL}user/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error('Error al Actualizar los Datos: ');
  }
};

export {
  getDatosUser,
  PostDatosStockLaboratorio,
  getOneDatos,
  getUserView,
  putDatosUser,
  getPlantillaPruebas,
  getCatalogoPruebasLaboratorio,
  createPruebaLaboratorio,
  updatePruebaLaboratorio,
  deletePruebaLaboratorio,
  getChapaContextoLaboratorio,
  getDatosSerieLaboratorio,
  createEnsayoLaboratorio,
  getEnsayosLaboratorio,
  getEnsayoLaboratorioById,
  getResultadosVistaEnsayoLaboratorio,
  seleccionarPruebasEnsayoLaboratorio,
  guardarBloqueEnsayoLaboratorio,
  cerrarEnsayoLaboratorio,
  getResultadosLaboratorio,
  postResultadosLaboratorio,
};
